import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import {
  Controller,
  FieldErrors,
  Resolver,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import {
  SyncedTripLeg,
  TripLeg,
  TripLegStatusLogEntry,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import type {
  TripLegStatusUpdate,
  DriverAssignment,
} from '../../../data/tripleg.slice';
import { Provider } from '../../../data/models/provider.model';
import {
  DiscussionLineItem,
  TripLegDiscussionDecision,
} from '../../../data/models/trip-leg-discussion-decision.model';
import { TripLegRequestV2 } from '../../../data/requests/trip-legs';
import dateUtils from '../../../services/DateUtils';
import * as providerSlice from '../../../data/provider.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as driverSlice from '../../../data/driver.slice';
import * as uiSlice from '../../../data/ui.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLookups } from '../../../hooks/useLookups';
import { phoneUtils } from '../../../services/phoneUtils';
import { FullscreenDialogHeader } from '../../dialogs/FullscreenDialogHeader';
import Checkbox from '../../forms/Checkbox';
import TimeInput from '../../forms/TimeInput';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { TripLegExceptionBC } from '../../../data/models/trip-leg-exception.model';
import {
  DiscussionRejectReason,
  DiscussionRejectedReasons,
} from '../../../data/models/discussion-reject-reason.model';
import { DiscussionAcceptReason } from '../../../data/models/discussion-accept-reason.model';
import triplegService from '@/services/triplegService';
import { TimeZone } from '@/data/models/time-zone.model';
import { PassengerCareLevel } from '@/data/models/passenger-care-level.model';
import passengerService from '@/services/passengerService';
import { Passenger } from '@/data/models/passenger.model';

const PREFIX = 'DiscussionDialog';

const classes = {
  content: `${PREFIX}-content`,
  section: `${PREFIX}-section`,
  header: `${PREFIX}-header`,
  phone: `${PREFIX}-phone`,
  label: `${PREFIX}-label`,
  denySubsection: `${PREFIX}-denySubsection`,
  approveSubsection: `${PREFIX}-approveSubsection`,
  radio: `${PREFIX}-radio`,
  reason: `${PREFIX}-reason`,
  amount: `${PREFIX}-amount`,
  actions: `${PREFIX}-actions`,
  addButton: `${PREFIX}-addButton`,
  approvalRow: `${PREFIX}-approvalRow`,
  summary: `${PREFIX}-summary`,
  tripDetail: `${PREFIX}-tripDetail`,
  detailValue: `${PREFIX}-detailValue`,
  spacer: `${PREFIX}-spacer`,
  loadingOverlay: `${PREFIX}-loadingOverlay`,
};

const Root = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    margin: theme.spacing(3),
    paddingLeft: 0,
    paddingRight: 0,
  },
  [`& .${classes.section}`]: {
    width: 564,
    padding: theme.spacing(4),
  },
  [`& .${classes.header}`]: {
    marginBottom: theme.spacing(3),
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: 'rgba(1, 16, 18, 0.9)',
    paddingBottom: 0,
  },
  [`& .${classes.phone}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
  },
  [`& .${classes.label}`]: {
    marginTop: theme.spacing(2),
    fontWeight: 600,
  },
  [`& .${classes.radio}`]: {
    paddingBottom: theme.spacing(2),
    '& span': {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  [`& .${classes.denySubsection}`]: {
    display: 'flex',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  [`& .${classes.approveSubsection}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(4),
    '& button': {
      flexGrow: 0,
    },
  },
  [`& .${classes.reason}`]: {
    width: '200px',
  },
  [`& .${classes.amount}`]: {
    width: '120px',
  },
  [`& .${classes.addButton}`]: {
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: theme.spacing(0.5),
    height: '40px',
    '&>span.MuiButton-startIcon': {
      marginRight: 0,
    },
  },
  [`& .${classes.approvalRow}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.summary}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.tripDetail}`]: {
    display: 'flex',
    gap: theme.spacing(0.5),
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: 'rgba(1, 16, 18, 0.9)',
  },
  [`& .${classes.detailValue}`]: {
    fontWeight: 600,
  },
  [`& .${classes.spacer}`]: {
    height: theme.spacing(2),
  },
  [`& .${classes.loadingOverlay}`]: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1,
  },
}));

enum Disposition {
  none = '',
  deny = 'NOCHANGE',
  approve = 'APPROVECHANGE',
  notServiced = 'NOTSERVICED',
}

const OTHER_REASON_BC = 'OTHER';

const DISCUSSION_STATUSES = [
  { value: TripLegStatus.Completed, label: 'Completed' },
  { value: TripLegStatus.NoLoad, label: 'No Load' },
  { value: TripLegStatus.Canceled, label: 'Canceled' },
  { value: TripLegStatus.CanceledLate, label: 'Late Cancel' },
];

interface DiscussionApproval {
  reasonBC: string;
  amount: number;
  otherReason?: string;
}

interface DiscussionFormValues {
  disposition: Disposition;
  denyReason: string;
  denyOtherReason: string;
  approveReason: string;
  approveOtherReason: string;
  notServicedReason: string;
  notServicedOtherReason: string;
  tripDetailsEnabled: boolean;
  tripDetailsStatus: TripLegStatus;
  tripDetailsDriver?: number;
  tripDetailsOnSiteTime: DateTime | null;
  tripDetailsOnBoardTime: DateTime | null;
  tripDetailsCompletedTime: DateTime | null;
  tripDetailsNoLoadTime: DateTime | null;
  tripDetailsCanceledTime: DateTime | null;
  tripDetailsStatusChangeReason: string;
  tripPricingEnabled: boolean;
  approveReasons: DiscussionApproval[];
}

const defaultValues = (tripLeg: SyncedTripLeg): DiscussionFormValues => ({
  disposition: Disposition.none,
  denyReason: '',
  denyOtherReason: '',
  approveReason: '',
  approveOtherReason: '',
  notServicedReason: '',
  notServicedOtherReason: '',
  tripDetailsEnabled: false,
  tripDetailsStatus: TripLegStatus.Draft,
  tripDetailsDriver: tripLeg.driverID!,
  tripDetailsOnSiteTime: null,
  tripDetailsOnBoardTime: null,
  tripDetailsCompletedTime: null,
  tripDetailsNoLoadTime: null,
  tripDetailsCanceledTime: null,
  tripDetailsStatusChangeReason: '',
  tripPricingEnabled: false,
  approveReasons: [{ reasonBC: '', amount: 0, otherReason: '' }],
});

const LabelValue = ({
  label,
  value,
  isDistance = false,
}: {
  label: string;
  value: string;
  isDistance?: boolean;
}) => {
  const { t } = useTranslation('common');
  const displayValue = isDistance ? Number(value).toFixed(1) : value;
  return (
    <Box className={classes.tripDetail}>
      <Box>{label}:</Box>
      <Box className={classes.detailValue}>
        {displayValue}
        {isDistance ? ` ${t('discussion.miles')}` : ''}
      </Box>
    </Box>
  );
};

interface DiscussionDialogProps {
  tripLeg: SyncedTripLeg;
  passenger: Passenger;
  open: boolean;
  close: () => void;
}

const validateDiscussionForm =
  (t: (s: string) => string): Resolver<DiscussionFormValues> =>
  (values) => {
    const errors: FieldErrors<DiscussionFormValues> = {};
    const msg = (message: string) => ({ type: 'validate' as const, message });
    const setError = <K extends keyof DiscussionFormValues>(
      key: K,
      message: string,
    ) => {
      errors[key] = msg(message) as FieldErrors<DiscussionFormValues>[K];
    };

    const {
      disposition,
      denyReason,
      denyOtherReason,
      approveReason,
      approveOtherReason,
      notServicedReason,
      notServicedOtherReason,
      tripDetailsEnabled,
      tripDetailsStatus,
      tripDetailsStatusChangeReason,
      tripDetailsDriver,
      tripDetailsOnSiteTime,
      tripDetailsOnBoardTime,
      tripDetailsCompletedTime,
      tripDetailsNoLoadTime,
      tripPricingEnabled,
      approveReasons,
    } = values;

    if (disposition === Disposition.deny) {
      if (!denyReason) {
        setError('denyReason', t('common.required'));
      } else if (denyReason === OTHER_REASON_BC && !denyOtherReason) {
        setError('denyOtherReason', t('common.required'));
      }
    }

    if (disposition === Disposition.notServiced) {
      if (!notServicedReason) {
        setError('notServicedReason', t('common.required'));
      } else if (
        notServicedReason === OTHER_REASON_BC &&
        !notServicedOtherReason
      ) {
        setError('notServicedOtherReason', t('common.required'));
      }
    }

    if (disposition === Disposition.approve) {
      if (!approveReason && !approveReasons?.length) {
        setError('approveReason', t('common.required'));
      } else if (approveReason === OTHER_REASON_BC && !approveOtherReason) {
        setError('approveOtherReason', t('common.required'));
      }

      if (!tripDetailsEnabled && !tripPricingEnabled) {
        setError(
          'disposition',
          t('discussion.mustEnableTripDetailsOrTripPricing'),
        );
      }

      if (tripDetailsEnabled) {
        if (!tripDetailsStatus) {
          setError('tripDetailsStatus', t('common.required'));
        }

        // Validate fields based on the selected status
        if (tripDetailsStatus === TripLegStatus.Completed) {
          if (!tripDetailsDriver) {
            setError('tripDetailsDriver', t('common.required'));
          }
          if (!tripDetailsOnSiteTime) {
            setError('tripDetailsOnSiteTime', t('common.required'));
          }
          if (!tripDetailsOnBoardTime) {
            setError('tripDetailsOnBoardTime', t('common.required'));
          }
          if (!tripDetailsCompletedTime) {
            setError('tripDetailsCompletedTime', t('common.required'));
          }
        }

        if (tripDetailsStatus === TripLegStatus.NoLoad) {
          if (!tripDetailsDriver) {
            setError('tripDetailsDriver', t('common.required'));
          }
          if (!tripDetailsOnSiteTime) {
            setError('tripDetailsOnSiteTime', t('common.required'));
          }
          if (!tripDetailsNoLoadTime) {
            setError('tripDetailsNoLoadTime', t('common.required'));
          }
        }

        if (tripDetailsStatus === TripLegStatus.CanceledLate) {
          // None required
        }
      }

      if (tripPricingEnabled) {
        if (!approveReasons?.length) {
          setError('approveReasons', 'At least one reason required');
        } else {
          const reasonErrors: Array<FieldErrors<DiscussionApproval>> = [];
          approveReasons.forEach((r, idx) => {
            const reasonError: FieldErrors<DiscussionApproval> = {};
            if (!r.reasonBC) {
              reasonError.reasonBC = msg(t('common.required'));
            }
            if ((r.amount ?? 0) <= 0) {
              reasonError.amount = msg('Must be greater than 0');
            }
            if (r.reasonBC === OTHER_REASON_BC && !r.otherReason) {
              reasonError.otherReason = msg(t('common.required'));
            }
            if (Object.keys(reasonError).length > 0) {
              reasonErrors[idx] = reasonError;
            }
          });
          if (reasonErrors.length > 0) {
            errors.approveReasons =
              reasonErrors as FieldErrors<DiscussionFormValues>['approveReasons'];
          }
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      return { values: {}, errors };
    }

    return { values, errors: {} };
  };

export function DiscussionDialog({
  tripLeg,
  passenger,
  open,
  close,
}: DiscussionDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const { control, handleSubmit, watch, reset, formState } =
    useForm<DiscussionFormValues>({
      resolver: validateDiscussionForm(t),
      mode: 'onChange',
      defaultValues: defaultValues(tripLeg),
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'approveReasons',
  });

  const formValues = watch();
  const { isValid } = formState;

  // This dialog is the only consumer of the discussion reason lookups and
  // `tripLegException`, which used to be fetched eagerly for every user in
  // `Home` on boot.
  useLookups([
    'discussionAcceptReason',
    'discussionRejectReason',
    'discussionRejectedReasons',
    'passengerCareLevel',
    'timeZone',
    'tripLegException',
    'tripMode',
  ]);

  const provider = useAppSelector(providerSlice.selectProvider);
  const tripModes = useAppSelector(lookupSlice.selectTripModes);
  const discussionAcceptReasons = useAppSelector(
    lookupSlice.selectLookupItems<DiscussionAcceptReason>(
      'discussionAcceptReason',
    ),
  );
  const discussionRejectReasons = useAppSelector(
    lookupSlice.selectLookupItems<DiscussionRejectReason>(
      'discussionRejectReason',
    ),
  );
  const timeZones = useAppSelector(
    lookupSlice.selectLookupItems<TimeZone>('timeZone'),
  );
  const getTimeZoneById = (timeZoneID: number) =>
    timeZones.find((tz) => tz.timeZoneID === timeZoneID)?.ianaStandard;
  const timeZoneIANA = useMemo(
    () => getTimeZoneById(tripLeg.appointmentTimeZoneID),
    [tripLeg.appointmentTimeZoneID],
  );

  const passengerCareLevels = useAppSelector(
    lookupSlice.selectLookupItems<PassengerCareLevel>('passengerCareLevel'),
  );
  const getPassengerCareLevelById = (passengerCareLevelID: number) =>
    passengerCareLevels.find(
      (tz) => tz.passengerCareLevelID === passengerCareLevelID,
    );

  const discussionRejectedReasons = useAppSelector(
    lookupSlice.selectLookupItems<DiscussionRejectedReasons>(
      'discussionRejectedReasons',
    ),
  );
  const tripLegAdjustmentTypes = useAppSelector(
    lookupSlice.selectTripLegDiscussionTypes,
  );
  const discussionTrip = useAppSelector(triplegSlice.selectTripLegDiscussion);
  const clearingDiscussion = useAppSelector(
    triplegSlice.selectClearingDiscussion,
  );
  const clearedDiscussion = useAppSelector(
    triplegSlice.selectClearedDiscussion,
  );
  const drivers = useAppSelector((state) =>
    tripLeg?.transportationProviderID
      ? driverSlice.selectDriversByProvider(
          state,
          tripLeg.transportationProviderID,
        )
      : [],
  );
  useEffect(() => {
    if (tripLeg?.transportationProviderID) {
      dispatch(
        providerSlice.fetchProviderAsync(tripLeg.transportationProviderID),
      );
      dispatch(
        driverSlice.fetchDriversByProviderAsync(
          tripLeg.transportationProviderID,
        ),
      );
    }
    if (tripLeg?.tripLegID) {
      dispatch(triplegSlice.getDiscussionAsync(tripLeg.tripLegID));
    }
  }, [tripLeg, dispatch]);

  useEffect(() => {
    if (open) {
      reset(defaultValues(tripLeg));
    }
  }, [open, tripLeg, reset]);

  useEffect(() => {
    if (clearedDiscussion) {
      handleClose();
    }
  }, [clearedDiscussion]);

  const getOwnerName = (provider: Provider | null) => {
    return provider
      ? `${provider.ownerFirstName || ''} ${provider.ownerLastName || ''}`
      : '';
  };

  const getOwnerPhone = (provider: Provider | null) => {
    let ownerPhone = '';
    if (provider?.phoneNumbers) {
      const phone = provider?.phoneNumbers.find(
        (pn) =>
          pn.contactLastName === provider.ownerLastName &&
          pn.contactFirstName === provider.ownerFirstName,
      );
      if (phone) {
        ownerPhone = phoneUtils.formatPhone(phone);
      }
    }
    return ownerPhone;
  };

  const getTripMode = (tripModeID: number, pclName: string) => {
    const tm = tripModes.find(
      (mode) => mode.tripModeID === tripModeID,
    )?.tripModeName;
    const vals = [tm, pclName].filter((val) => !!val);
    return vals.join(', ');
  };

  const getPickupTime = (tripLeg: SyncedTripLeg): string => {
    if (tripLeg?.isWillCall) {
      return t('discussion.willCall');
    }
    return dateUtils.formatPickupDateTime(
      tripLeg?.pickUpDueTimeUTC,
      getTimeZoneById(tripLeg.pickUpAddressTimeZoneID) || '',
    );
  };

  const tripLegExceptionItems = useAppSelector(
    lookupSlice.selectTripLegExceptions,
  );
  const exception = tripLegExceptionItems.find(
    (item) => item.tripLegExceptionBC === TripLegExceptionBC.ReadyInvoice,
  );

  const onSubmit = (data: DiscussionFormValues) => {
    if (
      data.tripDetailsStatus === TripLegStatus.NoLoad &&
      data.tripDetailsOnSiteTime &&
      data.tripDetailsNoLoadTime &&
      data.tripDetailsNoLoadTime <= data.tripDetailsOnSiteTime
    ) {
      dispatch(
        uiSlice.setErrorMessage(
          t(
            data.tripDetailsNoLoadTime < data.tripDetailsOnSiteTime
              ? 'discussion.noLoadBeforeOnSite'
              : 'discussion.noLoadLaterOnSite',
          ),
        ),
      );
      return;
    }

    if (
      data.tripDetailsStatus === TripLegStatus.Completed &&
      data.tripDetailsOnSiteTime &&
      data.tripDetailsOnBoardTime &&
      data.tripDetailsCompletedTime
    ) {
      if (data.tripDetailsOnBoardTime <= data.tripDetailsOnSiteTime) {
        dispatch(
          uiSlice.setErrorMessage(t('discussion.noOnBoardBeforeOnSite')),
        );
        return;
      } else if (data.tripDetailsCompletedTime <= data.tripDetailsOnBoardTime) {
        dispatch(
          uiSlice.setErrorMessage(t('discussion.noCompleteBeforeOnBoard')),
        );
        return;
      }
    }

    const { disposition } = data;
    let addOnLineItems: DiscussionLineItem[];

    if (disposition === Disposition.approve) {
      addOnLineItems = data.approveReasons.map((r) => ({
        tripLegAdjustmentType: r.reasonBC,
        amount: r.amount,
        description:
          r.reasonBC !== OTHER_REASON_BC
            ? tripLegAdjustmentTypes.find(
                (ct) => ct.tripLegAdjustmentTypeBC === r.reasonBC,
              )?.description
            : r.otherReason,
      }));
    } else if (disposition === Disposition.deny) {
      const description =
        data.denyReason === OTHER_REASON_BC ? data.denyOtherReason : undefined;
      addOnLineItems = [
        {
          tripLegAdjustmentType: data.denyReason,
          amount: 0.0,
          description,
        },
      ];
    } else {
      const description =
        data.notServicedReason === OTHER_REASON_BC
          ? data.notServicedOtherReason
          : undefined;
      addOnLineItems = [
        {
          tripLegAdjustmentType: data.notServicedReason,
          amount: 0.0,
          description,
        },
      ];
    }

    const decision: TripLegDiscussionDecision = {
      discussionDecisionOutcome: disposition,
      addOnLineItems,
    };

    if (disposition === Disposition.notServiced) {
      return dispatch(
        triplegSlice.submitAdjustTrip(
          tripLeg?.tripLegID!,
          decision,
          tripLeg?.status,
          undefined,
          undefined,
          {
            passengerUUID: passenger.passengerUUID!,
            tripLegReferenceID: tripLeg!.tripLegReferenceID!,
            tripLegRejectedReasonBC: data.notServicedReason,
          },
        ),
      );
    }

    if (disposition === Disposition.deny) {
      return dispatch(
        triplegSlice.clearUnderDiscussion(
          tripLeg?.tripLegID!,
          decision,
          exception?.tripLegExceptionID,
        ),
      );
    }

    if (disposition === Disposition.approve) {
      let tripLegStatusUpdate: TripLegStatusUpdate | undefined;
      let driverAssignment: DriverAssignment | undefined;

      if (data.tripDetailsEnabled && data.tripDetailsStatus) {
        const tripLegTimeZone = timeZones.find(
          (tz) => tz.timeZoneID === tripLeg?.pickUpAddressTimeZoneID,
        )?.ianaStandard;
        // Combine the time from a DateTime with the date from the trip's appointmentDate
        const applyApptDate = (
          time: DateTime | null | undefined,
        ): string | undefined => {
          if (!time || !tripLeg?.appointmentDate) return undefined;
          const apptDate = DateTime.fromISO(tripLeg.appointmentDate, {
            zone: tripLegTimeZone,
          });
          const resolvedTime = tripLegTimeZone
            ? time.setZone(tripLegTimeZone, { keepLocalTime: true })
            : time;
          return resolvedTime
            .set({
              year: apptDate.year,
              month: apptDate.month,
              day: apptDate.day,
            })
            .toUTC()
            .toISO()!;
        };

        let timestamp: DateTime | null = null;
        if (data.tripDetailsStatus === TripLegStatus.Completed) {
          timestamp = data.tripDetailsCompletedTime;
        } else if (data.tripDetailsStatus === TripLegStatus.NoLoad) {
          timestamp = data.tripDetailsNoLoadTime;
        } else if (data.tripDetailsStatus === TripLegStatus.CanceledLate) {
          timestamp = DateTime.fromISO(tripLeg!.pickUpDueTimeUTC!, {
            zone: tripLegTimeZone,
          })
            .minus({ minutes: 120 })
            .toUTC();
        } else if (data.tripDetailsStatus === TripLegStatus.Canceled) {
          timestamp = DateTime.fromISO(tripLeg!.pickUpDueTimeUTC!, {
            zone: tripLegTimeZone,
          })
            .minus({ minutes: 119 })
            .toUTC();
        }

        tripLegStatusUpdate = {
          passengerUUID: passenger.passengerUUID!,
          tripLegReferenceID: tripLeg!.tripLegReferenceID!,
          action: data.tripDetailsStatus,
          tripLegStatusReasonBCs: data.tripDetailsStatusChangeReason
            ? [data.tripDetailsStatusChangeReason]
            : undefined,
          onSiteDateTimeUTC: applyApptDate(data.tripDetailsOnSiteTime),
          onBoardDateTimeUTC: applyApptDate(data.tripDetailsOnBoardTime),
          completedDateTimeUTC: applyApptDate(data.tripDetailsCompletedTime),
          noLoadDateTimeUTC: applyApptDate(data.tripDetailsNoLoadTime),
          cancellationDateTimeUTC:
            data.tripDetailsStatus === TripLegStatus.Canceled ||
            data.tripDetailsStatus === TripLegStatus.CanceledLate
              ? (timestamp?.toISO() ?? undefined)
              : (applyApptDate(timestamp) ?? undefined),
        };

        if (data.tripDetailsDriver) {
          driverAssignment = {
            tripLegReferenceID: tripLeg!.tripLegReferenceID!,
            driverID: Number(data.tripDetailsDriver),
          };
        }

        return dispatch(
          triplegSlice.submitAdjustTrip(
            tripLeg?.tripLegID!,
            decision,
            tripLeg?.status,
            tripLegStatusUpdate,
            driverAssignment,
            undefined,
            exception?.tripLegExceptionID,
          ),
        );
      }
    }
    return dispatch(
      triplegSlice.clearUnderDiscussion(
        tripLeg?.tripLegID!,
        decision,
        exception?.tripLegExceptionID,
      ),
    );
  };

  const handleClose = () => {
    reset(defaultValues(tripLeg));
    close();
  };

  const formatAddress = (addrParts: string[]) => {
    return addrParts.filter((p) => !!p).join(', ');
  };

  const formatPickupAddress = (tripLeg: any) => {
    const parts = [
      tripLeg?.pickUpAddressLine1,
      tripLeg?.pickUpAddressLine2,
      tripLeg?.pickUpCity,
      tripLeg?.pickUpStateCode,
      tripLeg?.pickUpZipCode?.substring(0, 5),
    ];
    return formatAddress(parts);
  };

  const formatDropoffAddress = (tripLeg: any) => {
    const parts = [
      tripLeg?.dropOffAddressLine1,
      tripLeg?.dropOffAddressLine2,
      tripLeg?.dropOffCity,
      tripLeg?.dropOffStateCode,
      tripLeg?.dropOffZipCode?.substring(0, 5),
    ];
    return formatAddress(parts);
  };

  const getStatusTime = (
    status: TripLegStatus,
    logs?: TripLegStatusLogEntry[],
    timeZone?: string,
  ) => {
    const match = (logs || []).find(
      (log) => log.tripLegStatusBC.toLowerCase() === String(status),
    );

    return match
      ? dateUtils.formatDateTime(
          DateTime.fromISO(match.createdOnUTC, { zone: timeZone || 'utc' }),
        )
      : '';
  };

  const formatDiscussionReasons = (tl: TripLegRequestV2 | null) => {
    if (tl?.tripLegDiscussions) {
      return tl.tripLegDiscussions.reduce((prev, cur) => {
        return `${prev} ${prev.length ? ', ' : ''}${cur.discussionReason}`;
      }, '');
    }
    return '';
  };

  return (
    <Root fullScreen open={open} onClose={handleClose}>
      <FullscreenDialogHeader
        title={t('discussion.discuss')}
        subTitle={tripLeg?.tripLegReferenceID}
        close={handleClose}
      />
      {clearingDiscussion && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress />
        </Box>
      )}
      <DialogContent className={classes.content}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Paper className={classes.section}>
            <Box className={classes.header}>
              <Typography variant="h3">
                {tripLeg?.transportationProvider?.transportationProviderName ??
                  ''}
              </Typography>
              <Typography variant="inherit">
                {getOwnerName(provider)}
              </Typography>
              <Typography variant="inherit" className={classes.phone}>
                {getOwnerPhone(provider)}
              </Typography>
              <Typography variant="inherit" className={classes.label}>
                {t('discussion.discussReason')}
              </Typography>
              <Typography variant="inherit">
                {formatDiscussionReasons(discussionTrip)}
              </Typography>
              <Typography variant="inherit" className={classes.label}>
                {t('discussion.outcome')}
              </Typography>
            </Box>
            <Box>
              <Controller
                name="disposition"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    aria-label="driver location"
                    name="driver-location"
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value as Disposition)
                    }>
                    <FormControlLabel
                      className={classes.radio}
                      value={Disposition.deny}
                      control={<Radio />}
                      label={t('discussion.deny')}
                      sx={{ align: 'center' }}
                    />
                    {formValues.disposition === Disposition.deny && (
                      <Box className={classes.denySubsection}>
                        <Controller
                          name="denyReason"
                          control={control}
                          render={({ field: reasonField }) => (
                            <TextField
                              select
                              required
                              className={classes.reason}
                              label={t('discussion.reason')}
                              variant="outlined"
                              value={reasonField.value}
                              onChange={reasonField.onChange}
                              onBlur={reasonField.onBlur}>
                              {discussionRejectReasons.map((val, idx) => (
                                <MenuItem
                                  value={val.discussionRejectReasonBC}
                                  key={idx}>
                                  {val.discussionRejectReasonName}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                        {formValues.denyReason === OTHER_REASON_BC && (
                          <Controller
                            name="denyOtherReason"
                            control={control}
                            render={({ field: otherField }) => (
                              <TextField
                                required
                                label={t('discussion.otherReason')}
                                value={otherField.value}
                                onChange={otherField.onChange}
                                onBlur={otherField.onBlur}
                              />
                            )}
                          />
                        )}
                      </Box>
                    )}
                    <FormControlLabel
                      className={classes.radio}
                      value={Disposition.approve}
                      control={<Radio />}
                      label={t('discussion.adjust')}
                    />
                    {formValues.disposition === Disposition.approve && (
                      <Box className={classes.approveSubsection}>
                        <Checkbox
                          name="tripDetailsEnabled"
                          control={control}
                          label={t('discussion.tripDetails')}
                        />
                        {formValues.tripDetailsEnabled && (
                          <Box className={classes.approveSubsection}>
                            <Box className={classes.denySubsection}>
                              <Controller
                                name="approveReason"
                                control={control}
                                render={({ field: reasonField }) => (
                                  <TextField
                                    select
                                    required
                                    className={classes.reason}
                                    label={t('discussion.reason')}
                                    variant="outlined"
                                    value={reasonField.value}
                                    onChange={reasonField.onChange}
                                    onBlur={reasonField.onBlur}>
                                    {discussionAcceptReasons.map((val, idx) => (
                                      <MenuItem
                                        value={val.discussionAcceptReasonBC}
                                        key={idx}>
                                        {val.discussionAcceptReasonName}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              />
                              {formValues.approveReason === OTHER_REASON_BC && (
                                <Controller
                                  name="approveOtherReason"
                                  control={control}
                                  render={({ field: otherField }) => (
                                    <TextField
                                      required
                                      label={t('discussion.otherReason')}
                                      value={otherField.value}
                                      onChange={otherField.onChange}
                                      onBlur={otherField.onBlur}
                                    />
                                  )}
                                />
                              )}
                            </Box>
                            <Controller
                              name="tripDetailsStatus"
                              control={control}
                              render={({ field: statusField }) => (
                                <TextField
                                  select
                                  required
                                  className={classes.reason}
                                  label={t('discussion.status')}
                                  variant="outlined"
                                  value={statusField.value}
                                  onChange={statusField.onChange}
                                  onBlur={statusField.onBlur}>
                                  {DISCUSSION_STATUSES.filter(
                                    (s) =>
                                      !(
                                        s.value ===
                                          TripLegStatus.CanceledLate &&
                                        tripLeg?.isWillCall
                                      ),
                                  ).map((s) => (
                                    <MenuItem value={s.value} key={s.value}>
                                      {s.label}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            />
                            {!!formValues.tripDetailsStatus && (
                              <>
                                {formValues.tripDetailsStatus ===
                                  TripLegStatus.Completed && (
                                  <>
                                    <Controller
                                      name="tripDetailsDriver"
                                      control={control}
                                      render={({ field: driverField }) => (
                                        <TextField
                                          select
                                          required
                                          className={classes.reason}
                                          label={t('discussion.driver')}
                                          variant="outlined"
                                          value={driverField.value}
                                          onChange={driverField.onChange}
                                          onBlur={driverField.onBlur}>
                                          {drivers.map((d) => (
                                            <MenuItem
                                              value={d.driverID}
                                              key={d.driverID}>
                                              {`${d.driverFirstName || ''} ${d.driverLastName || ''}`}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      )}
                                    />
                                    <TimeInput
                                      name="tripDetailsOnSiteTime"
                                      label={t('discussion.onsite')}
                                      control={control}
                                      required
                                    />
                                    <TimeInput
                                      name="tripDetailsOnBoardTime"
                                      label={t('discussion.onboard')}
                                      control={control}
                                      required
                                    />
                                    <TimeInput
                                      name="tripDetailsCompletedTime"
                                      label={t('discussion.completed')}
                                      control={control}
                                      required
                                    />
                                  </>
                                )}
                                {formValues.tripDetailsStatus ===
                                  TripLegStatus.NoLoad && (
                                  <>
                                    <Controller
                                      name="tripDetailsDriver"
                                      control={control}
                                      render={({ field: driverField }) => (
                                        <TextField
                                          select
                                          required
                                          className={classes.reason}
                                          label={t('discussion.driver')}
                                          variant="outlined"
                                          value={driverField.value}
                                          onChange={driverField.onChange}
                                          onBlur={driverField.onBlur}>
                                          {drivers.map((d) => (
                                            <MenuItem
                                              value={d.driverID}
                                              key={d.driverID}>
                                              {`${d.driverFirstName || ''} ${d.driverLastName || ''}`}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      )}
                                    />
                                    <TimeInput
                                      name="tripDetailsOnSiteTime"
                                      label={t('discussion.onsite')}
                                      control={control}
                                      required
                                    />
                                    <TimeInput
                                      name="tripDetailsNoLoadTime"
                                      label={t('discussion.noLoad')}
                                      control={control}
                                      required
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </Box>
                        )}
                        <Checkbox
                          name="tripPricingEnabled"
                          control={control}
                          label={t('discussion.tripPricing')}
                        />
                        {formValues.tripPricingEnabled && (
                          <Box className={classes.approveSubsection}>
                            {fields.map((field, index) => (
                              <Box
                                key={field.id}
                                className={classes.approvalRow}>
                                <Controller
                                  name={`approveReasons.${index}.reasonBC`}
                                  control={control}
                                  render={({ field: reasonField }) => (
                                    <TextField
                                      select
                                      required
                                      className={classes.reason}
                                      label={t('discussion.reason')}
                                      variant="outlined"
                                      value={reasonField.value}
                                      onChange={reasonField.onChange}
                                      onBlur={reasonField.onBlur}>
                                      {tripLegAdjustmentTypes.map(
                                        (val, idx) => (
                                          <MenuItem
                                            value={val.tripLegAdjustmentTypeBC}
                                            key={idx}>
                                            {val.tripLegAdjustmentTypeName}
                                          </MenuItem>
                                        ),
                                      )}
                                    </TextField>
                                  )}
                                />
                                <Controller
                                  name={`approveReasons.${index}.amount`}
                                  control={control}
                                  render={({ field: amountField }) => (
                                    <TextField
                                      required
                                      className={classes.amount}
                                      type="number"
                                      value={amountField.value}
                                      onChange={(e) =>
                                        amountField.onChange(
                                          (e.target as HTMLInputElement)
                                            .valueAsNumber || '',
                                        )
                                      }
                                      onBlur={amountField.onBlur}
                                    />
                                  )}
                                />
                                {formValues.approveReasons?.[index]
                                  ?.reasonBC === OTHER_REASON_BC && (
                                  <Controller
                                    name={`approveReasons.${index}.otherReason`}
                                    control={control}
                                    render={({ field: otherField }) => (
                                      <TextField
                                        required
                                        label={t('discussion.otherReason')}
                                        value={otherField.value}
                                        onChange={otherField.onChange}
                                        onBlur={otherField.onBlur}
                                      />
                                    )}
                                  />
                                )}
                                <IconButton onClick={() => remove(index)}>
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Box>
                            ))}
                            <Button
                              className={classes.addButton}
                              startIcon={<AddIcon />}
                              onClick={() =>
                                append({
                                  reasonBC: '',
                                  amount: 0,
                                  otherReason: '',
                                })
                              }>
                              {t('discussion.addReason')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                    <FormControlLabel
                      className={classes.radio}
                      value={Disposition.notServiced}
                      control={<Radio />}
                      label={t('discussion.notServiced')}
                      sx={{ align: 'center' }}
                    />
                    {formValues.disposition === Disposition.notServiced && (
                      <Box className={classes.denySubsection}>
                        <Controller
                          name="notServicedReason"
                          control={control}
                          render={({ field: reasonField }) => (
                            <TextField
                              select
                              required
                              className={classes.reason}
                              label={t('discussion.reason')}
                              variant="outlined"
                              value={reasonField.value}
                              onChange={reasonField.onChange}
                              onBlur={reasonField.onBlur}>
                              {discussionRejectedReasons.map((val, idx) => (
                                <MenuItem
                                  value={val.discussionNotServicedReasonBC}
                                  key={idx}>
                                  {val.discussionNotServicedReasonName}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                        {formValues.notServicedReason === OTHER_REASON_BC && (
                          <Controller
                            name="notServicedOtherReason"
                            control={control}
                            render={({ field: otherField }) => (
                              <TextField
                                required
                                label={t('discussion.otherReason')}
                                value={otherField.value}
                                onChange={otherField.onChange}
                                onBlur={otherField.onBlur}
                              />
                            )}
                          />
                        )}
                      </Box>
                    )}
                  </RadioGroup>
                )}
              />
            </Box>
            <Box className={classes.actions}>
              <Button
                color="primary"
                variant="contained"
                disabled={!isValid || clearingDiscussion}
                onClick={handleSubmit(onSubmit)}
                data-fs-element="DiscussTripSubmitButton">
                {t('common.submit')}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </Box>
          </Paper>
          <Paper className={classes.section}>
            <Box className={classes.summary}>
              <Typography variant="h3">{t('discussion.summary')}</Typography>
              <LabelValue
                label={t('discussion.id')}
                value={tripLeg?.tripLegReferenceID || ''}
              />
              <LabelValue
                label={t('discussion.member')}
                value={`${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`}
              />
              <LabelValue
                label={t('discussion.tripMode')}
                value={getTripMode(
                  tripLeg.tripModeID,
                  getPassengerCareLevelById(tripLeg.passengerCareLevelID)!
                    .passengerCareLevelName,
                )}
              />
              <LabelValue
                label={t('discussion.pickupTime')}
                value={getPickupTime(tripLeg)}
              />
              <LabelValue
                label={t('discussion.pickupLocation')}
                value={formatPickupAddress(tripLeg)}
              />
              <LabelValue
                label={t('discussion.dropoffLocation')}
                value={formatDropoffAddress(tripLeg)}
              />
              <LabelValue
                label={t('discussion.distance')}
                value={tripLeg?.invoiceDistanceMiles || ''}
                isDistance={true}
              />
              <LabelValue
                label={t('discussion.price')}
                value={tripLeg?.payableAmount || ''}
              />
              <Box className={classes.spacer}>&nbsp;</Box>
              <LabelValue
                label={t('discussion.provider')}
                value={
                  tripLeg?.transportationProvider?.transportationProviderName ||
                  ''
                }
              />
              <LabelValue
                label={t('discussion.driver')}
                value={`${tripLeg.driver?.driverFirstName || ''} ${tripLeg.driver?.driverLastName || ''}`}
              />
              <LabelValue
                label={t('discussion.onsite')}
                value={getStatusTime(
                  TripLegStatus.Onsite,
                  tripLeg?.tripLegStatusLogs,
                  timeZoneIANA,
                )}
              />
              <LabelValue
                label={t('discussion.onboard')}
                value={getStatusTime(
                  TripLegStatus.Onboard,
                  tripLeg?.tripLegStatusLogs,
                  timeZoneIANA,
                )}
              />
              <LabelValue
                label={t('discussion.completed')}
                value={getStatusTime(
                  TripLegStatus.Completed,
                  tripLeg?.tripLegStatusLogs,
                  timeZoneIANA,
                )}
              />
            </Box>
          </Paper>
        </LocalizationProvider>
      </DialogContent>
    </Root>
  );
}

type DiscussionDialogWrapperProps = {
  tripLegReferenceID: string;
  passengerUUID: string;
  tripLegID: number;
  open: boolean;
  close: () => void;
};

const DiscussionDialogWrapper = ({
  tripLegReferenceID,
  tripLegID,
  passengerUUID,
  ...rest
}: DiscussionDialogWrapperProps) => {
  const [tripLeg, setTripLeg] = useState<SyncedTripLeg | null>(null);
  const [passenger, setPassenger] = useState<Passenger | null>(null);

  useEffect(() => {
    passengerService.getPassenger(passengerUUID).subscribe({
      next(value) {
        setPassenger(value);
      },
    });

    triplegService.getTripLeg({ tripLegReferenceID, tripLegID }).subscribe({
      next: (value) => {
        setTripLeg(value);
      },
    });
  }, [tripLegReferenceID, tripLegID]);

  if (!(tripLeg && passenger)) {
    return null;
  }

  return <DiscussionDialog {...rest} tripLeg={tripLeg} passenger={passenger} />;
};

export default DiscussionDialogWrapper;
